"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "@/components/Container";
import { MaskReveal } from "@/components/Motion";
import { testimonials as staticTestimonials } from "@/lib/home";

// Quotes come from the DB via /admin/testimonials (req 1.7); the static list
// is the fallback when no items are passed (e.g. DB unset/unreachable).
export type TestimonialItem = {
  quote: string;
  name: string;
  detail: string | null;
  image: string | null;
};

export function Testimonials({ items }: { items?: TestimonialItem[] } = {}) {
  const testimonials: TestimonialItem[] =
    items && items.length > 0 ? items : staticTestimonials;

  // Horizontal scroller — 3 cards visible on desktop; anything beyond scrolls,
  // driven by the two themed arrows (and native swipe on touch).
  const scroller = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update, testimonials.length]);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    const card = el.querySelector("figure");
    const step = card ? card.getBoundingClientRect().width + 20 /* gap-5 */ : el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // Arrows only appear when there is more to scroll than fits (i.e. > 3 items).
  const showArrows = testimonials.length > 3;

  const arrowCls =
    "flex h-11 w-11 items-center justify-center rounded-full border border-brand-line text-brand-ink transition-colors hover:border-brand-accent hover:text-brand-accent disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-brand-line disabled:hover:text-brand-ink";

  return (
    <section className="border-t border-brand-line bg-brand-paper text-brand-ink">
      <Container className="py-20 md:py-28">
        <div className="flex items-start justify-between gap-6">
          <h2 className="max-w-3xl t-display text-brand-ink">
            <MaskReveal lines={[<span key="t">Hear From Our <span className="text-brand-accent">Clients</span></span>]} />
          </h2>

          {showArrows && (
            <div className="hidden shrink-0 gap-3 sm:flex">
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                disabled={!canLeft}
                aria-label="Previous testimonials"
                className={arrowCls}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                disabled={!canRight}
                aria-label="Next testimonials"
                className={arrowCls}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div
          ref={scroller}
          onScroll={update}
          className="-mx-1 mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto px-1 pb-4 md:mt-14 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {testimonials.map((t) => (
            // cards are not clickable — no hover effect (req 1.6); no image (per Figma "Portfolio")
            <figure
              key={t.name}
              className="flex min-h-[360px] w-[82%] shrink-0 snap-start flex-col justify-between rounded-2xl border border-brand-accent-bright/60 bg-brand-paper p-7 md:min-h-[400px] md:p-8 sm:w-[46%] lg:w-[calc((100%-2.5rem)/3)]"
            >
              <blockquote className="text-base font-normal leading-relaxed text-brand-ink">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-8">
                <span className="block text-sm font-normal text-brand-ink">
                  {t.name}
                </span>
                {t.detail && (
                  <span className="mono-meta text-brand-muted">
                    {t.detail.toUpperCase()}
                  </span>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </Container>
    </section>
  );
}
